import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Inspection, InspectionItem, LoadingState } from '@types'
import { inspectionsApi } from '@api/inspections'

interface InspectionsState {
  inspections: Inspection[]
  selectedInspection: Inspection | null
  loadingState: LoadingState
  error: string | null
  inspectionEdited: boolean
}

const initialState: InspectionsState = {
  inspections: [],
  selectedInspection: null,
  loadingState: 'idle',
  error: null,
  inspectionEdited: false,
}

// Async thunks
export const fetchInspections = createAsyncThunk<Inspection[], { hiveId?: number }>(
  'inspections/fetchInspections',
  async ({ hiveId }: { hiveId?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await inspectionsApi.getInspections(hiveId)
      const raw = response.data.data
      // Hive-scoped: { hive_id, hive_name, inspections }
      // List endpoint: paginated { results } or a bare array.
      if (Array.isArray(raw)) return raw
      if (Array.isArray((raw as any)?.inspections)) return (raw as any).inspections
      if (Array.isArray((raw as any)?.results)) return (raw as any).results
      return []
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch inspections')
    }
  }
)

export const fetchInspection = createAsyncThunk<Inspection, number>(
  'inspections/fetchInspection',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await inspectionsApi.getInspection(id)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch inspection')
    }
  }
)

export const createInspection = createAsyncThunk<Inspection, Partial<Inspection>>(
  'inspections/createInspection',
  async (inspectionData: Partial<Inspection>, { rejectWithValue }) => {
    try {
      const response = await inspectionsApi.createInspection(inspectionData)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create inspection')
    }
  }
)

export const updateInspection = createAsyncThunk<Inspection, { id: number; data: Partial<Inspection> }>(
  'inspections/updateInspection',
  async ({ id, data }: { id: number; data: Partial<Inspection> }, { rejectWithValue }) => {
    try {
      const response = await inspectionsApi.updateInspection(id, data)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update inspection')
    }
  }
)

export const deleteInspection = createAsyncThunk(
  'inspections/deleteInspection',
  async (id: number, { rejectWithValue }) => {
    try {
      await inspectionsApi.deleteInspection(id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete inspection')
    }
  }
)

const inspectionsSlice = createSlice({
  name: 'inspections',
  initialState,
  reducers: {
    setSelectedInspection: (state, action: PayloadAction<Inspection | null>) => {
      state.selectedInspection = action.payload
    },
    setInspectionEdited: (state, action: PayloadAction<boolean>) => {
      state.inspectionEdited = action.payload
    },
    updateInspectionItem: (state, action: PayloadAction<{ itemId: number; value: any }>) => {
      if (state.selectedInspection) {
        const item = state.selectedInspection.items.find(i => i.id === action.payload.itemId)
        if (item) {
          item.value = action.payload.value
        }
        state.inspectionEdited = true
      }
    },
    addInspectionItem: (state, action: PayloadAction<InspectionItem>) => {
      if (state.selectedInspection) {
        state.selectedInspection.items.push(action.payload)
        state.inspectionEdited = true
      }
    },
    removeInspectionItem: (state, action: PayloadAction<number>) => {
      if (state.selectedInspection) {
        state.selectedInspection.items = state.selectedInspection.items.filter(
          item => item.id !== action.payload
        )
        state.inspectionEdited = true
      }
    },
    clearError: (state) => {
      state.error = null
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch Inspections
      .addCase(fetchInspections.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(fetchInspections.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.inspections = action.payload
      })
      .addCase(fetchInspections.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.payload as string
      })
      
      // Fetch Inspection
      .addCase(fetchInspection.fulfilled, (state, action) => {
        state.selectedInspection = action.payload
        // Also update in inspections array if it exists
        const index = state.inspections.findIndex(i => i.id === action.payload.id)
        if (index !== -1) {
          state.inspections[index] = action.payload
        }
      })
      
      // Create Inspection
      .addCase(createInspection.fulfilled, (state, action) => {
        state.inspections.unshift(action.payload)
        state.selectedInspection = action.payload
        state.inspectionEdited = false
      })
      
      // Update Inspection
      .addCase(updateInspection.fulfilled, (state, action) => {
        const index = state.inspections.findIndex(i => i.id === action.payload.id)
        if (index !== -1) {
          state.inspections[index] = action.payload
        }
        if (state.selectedInspection?.id === action.payload.id) {
          state.selectedInspection = action.payload
        }
        state.inspectionEdited = false
      })
      
      // Delete Inspection
      .addCase(deleteInspection.fulfilled, (state, action) => {
        state.inspections = state.inspections.filter(i => i.id !== action.payload)
        if (state.selectedInspection?.id === action.payload) {
          state.selectedInspection = null
        }
      })
  },
})

export const {
  setSelectedInspection,
  setInspectionEdited,
  updateInspectionItem,
  addInspectionItem,
  removeInspectionItem,
  clearError,
  resetState,
} = inspectionsSlice.actions

export default inspectionsSlice.reducer
